import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core';
import {
  AddRounded as AddIcon,
  DeleteRounded as DeleteIcon
} from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AuthContext, useSpot } from 'framework';
import {
  OnboardingDocument,
  OnboardingDocumentStatus,
  OnboardingOwnerType,
  OnboardingRequiredDocumentType,
  OnboardingRecord,
  OnboardingStatus
} from 'domain-types';
import {
  Capitalized,
  Container,
  FormFieldWrapper,
  FormLabel,
  FormRow,
  Gap,
  Line,
  TitleContainer,
  VerticalContainer
} from 'components';

const ScrollContainer = styled(Box)`
  max-height: 600px;
  overflow: auto;
`;

const SummaryRow = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: ${p => p.theme.spacing(1)}px;
  margin: ${p => p.theme.spacing(2, 0)};
`;

const Section = styled(Box)`
  margin: ${p => p.theme.spacing(3, 0)};
`;

const ItemRow = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${p => p.theme.spacing(1)}px;
  margin: ${p => p.theme.spacing(1, 0)};
`;

const InputGroup = styled(Box)`
  display: flex;
  flex: 1 1 280px;
  flex-wrap: wrap;
  gap: ${p => p.theme.spacing(1)}px;
`;

const NewItemRow = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${p => p.theme.spacing(1)}px;
  margin-top: ${p => p.theme.spacing(2)}px;
`;

const EmptyState = styled(Typography)`
  color: ${p => p.theme.palette.text.secondary};
`;

const normalizeOnboarding = (
  onboarding?: OnboardingRecord
): OnboardingRecord => ({
  status: onboarding?.status ?? 'not_started',
  checklist: onboarding?.checklist ?? [],
  documents: onboarding?.documents ?? []
});

const documentStatuses: OnboardingDocumentStatus[] = [
  'missing',
  'received',
  'approved'
];

const onboardingStatuses: OnboardingStatus[] = [
  'not_started',
  'in_progress',
  'completed'
];

const getStatusLabel = (t: (key: string) => string, status: OnboardingStatus) =>
  t(
    {
      not_started: 'onboardingStatusNotStarted',
      in_progress: 'onboardingStatusInProgress',
      completed: 'onboardingStatusCompleted'
    }[status]
  );

const getDocumentStatusLabel = (
  t: (key: string) => string,
  status: OnboardingDocumentStatus
) =>
  t(
    {
      missing: 'onboardingDocumentMissing',
      received: 'onboardingDocumentReceived',
      approved: 'onboardingDocumentApproved'
    }[status]
  );

const getRequiredDocumentLabel = (
  t: (key: string) => string,
  documentType: OnboardingRequiredDocumentType
) =>
  t(
    {
      id: 'onboardingRequiredDocumentId',
      signed_contract: 'onboardingRequiredDocumentSignedContract'
    }[documentType]
  );

const getDocumentTitle = (
  t: (key: string) => string,
  document: OnboardingDocument
) =>
  document.documentType
    ? getRequiredDocumentLabel(t, document.documentType)
    : document.title ?? '';

const getOwnerLabel = (
  t: (key: string) => string,
  ownerType: OnboardingOwnerType
) =>
  t(
    {
      administrators: 'onboardingOwnerAdministrators',
      managers: 'onboardingOwnerManagers'
    }[ownerType]
  );

const hasValidTitle = (value?: string) => (value ?? '').trim().length > 0;

const isOnboardingDraftValid = (onboarding: OnboardingRecord) =>
  onboarding.checklist.every(item => hasValidTitle(item.title)) &&
  (onboarding.documents ?? []).every(
    document => !!document.documentType || hasValidTitle(document.title)
  );

export function Onboarding({
  id,
  organization
}: {
  id: string;
  organization: string;
}) {
  const { t } = useTranslation();
  const { getGroups } = useContext(AuthContext);
  const { data, query, command } = useSpot();
  const detailUser = data?.users?.[id];
  const searchUser = data?.searchUsers?.[id];
  const user = useMemo(() => {
    if (!detailUser) {
      return searchUser;
    }

    if (!searchUser) {
      return detailUser;
    }

    const onboarding = detailUser.onboarding ?? searchUser.onboarding;

    return {
      ...searchUser,
      ...detailUser,
      onboarding
    };
  }, [detailUser, searchUser]);
  const normalizedOnboarding = useMemo(
    () => normalizeOnboarding(user?.onboarding),
    [user]
  );
  const [draft, setDraft] = useState<OnboardingRecord>(
    () => normalizedOnboarding
  );
  const [newChecklistTitle, setNewChecklistTitle] = useState('');
  const [newDocumentTitle, setNewDocumentTitle] = useState('');

  const groups = getGroups();
  const canManage =
    groups.includes('administrators') || groups.includes('managers');
  const newChecklistOwnerType: OnboardingOwnerType = groups.includes(
    'administrators'
  )
    ? 'administrators'
    : 'managers';

  useEffect(() => {
    setDraft(normalizedOnboarding);
  }, [normalizedOnboarding]);

  const refreshUser = useCallback(async () => {
    if (groups.includes('administrators')) {
      await query(`org/${organization}/user/${id}`, {}, ['users', id]);
      return;
    }

    await query(`org/${organization}/search`, {}, ['searchUsers']);
  }, [groups, id, organization, query]);

  const persistOnboarding = async (nextOnboarding: OnboardingRecord) => {
    setDraft(nextOnboarding);
    await command(`org/${organization}/user/${id}`, {
      onboarding: nextOnboarding
    });
    await refreshUser();
  };

  const updateChecklistItem = async (
    index: number,
    updates: Partial<OnboardingRecord['checklist'][number]>,
    persist = true
  ) => {
    const nextOnboarding = {
      ...draft,
      checklist: draft.checklist.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              ...updates
            }
          : item
      )
    };

    if (persist) {
      await persistOnboarding(nextOnboarding);
      return;
    }

    setDraft(nextOnboarding);
  };

  const updateDocument = async (
    index: number,
    updates: Partial<OnboardingDocument>,
    persist = true
  ) => {
    const nextOnboarding = {
      ...draft,
      documents: (draft.documents ?? []).map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              ...updates
            }
          : item
      )
    };

    if (persist) {
      await persistOnboarding(nextOnboarding);
      return;
    }

    setDraft(nextOnboarding);
  };

  const restoreChecklistTitle = (index: number) => {
    const persistedTitle = normalizedOnboarding.checklist[index]?.title;
    setDraft(currentDraft => ({
      ...currentDraft,
      checklist: currentDraft.checklist.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              title: persistedTitle ?? ''
            }
          : item
      )
    }));
  };

  const restoreDocumentTitle = (index: number) => {
    const persistedTitle = normalizedOnboarding.documents?.[index]?.title;
    setDraft(currentDraft => ({
      ...currentDraft,
      documents: (currentDraft.documents ?? []).map((document, itemIndex) =>
        itemIndex === index
          ? {
              ...document,
              title: persistedTitle ?? ''
            }
          : document
      )
    }));
  };

  const checklistDoneCount = draft.checklist.filter(item => item.done).length;
  const documentDoneCount = (draft.documents ?? []).filter(
    document => document.status === 'approved'
  ).length;
  const hasInvalidDraftTitles = canManage && !isOnboardingDraftValid(draft);

  return (
    <ScrollContainer>
      <Line />
      <Container>
        <FormRow>
          <FormLabel>{t('status')}</FormLabel>
          <FormFieldWrapper>
            <Select
              fullWidth
              value={draft.status}
              disabled={!canManage || hasInvalidDraftTitles}
              inputProps={{ 'aria-label': 'onboarding-status' }}
              onChange={event => {
                if (hasInvalidDraftTitles) {
                  return;
                }

                persistOnboarding({
                  ...draft,
                  status: event.target.value as OnboardingStatus
                });
              }}
            >
              {onboardingStatuses.map(status => (
                <MenuItem key={status} value={status}>
                  {getStatusLabel(t, status)}
                </MenuItem>
              ))}
            </Select>
            {hasInvalidDraftTitles && (
              <Typography color="error" variant="caption">
                {t('onboardingStatusFixTitles')}
              </Typography>
            )}
          </FormFieldWrapper>
        </FormRow>

        <SummaryRow>
          <Chip label={getStatusLabel(t, draft.status)} color="primary" />
          <Chip
            label={t('onboardingChecklistSummary', {
              done: checklistDoneCount,
              total: draft.checklist.length
            })}
          />
          <Chip
            label={t('onboardingDocumentSummary', {
              done: documentDoneCount,
              total: draft.documents?.length ?? 0
            })}
          />
        </SummaryRow>

        <Section>
          <TitleContainer>
            <Capitalized variant="h5">{t('onboardingChecklist')}</Capitalized>
          </TitleContainer>
          <Line />
          {draft.checklist.length === 0 && (
            <EmptyState variant="body2">
              {t('onboardingChecklistEmpty')}
            </EmptyState>
          )}
          {draft.checklist.map((item, index) => {
            const checklistTitle = item.title ?? '';
            const hasChecklistTitleError = canManage && !checklistTitle.trim();

            return (
              <ItemRow key={index}>
                <Checkbox
                  checked={item.done}
                  disabled={!canManage}
                  inputProps={{ 'aria-label': `checklist-${index}` }}
                  onChange={event =>
                    updateChecklistItem(index, { done: event.target.checked })
                  }
                />
                <InputGroup>
                  <TextField
                    label={t('onboardingChecklistItem')}
                    value={checklistTitle}
                    disabled={!canManage}
                    error={hasChecklistTitleError}
                    helperText={
                      hasChecklistTitleError
                        ? t('onboardingChecklistTitleRequired')
                        : ' '
                    }
                    fullWidth
                    inputProps={{ 'aria-label': `checklist-title-${index}` }}
                    onChange={event =>
                      updateChecklistItem(
                        index,
                        { title: event.target.value },
                        false
                      )
                    }
                    onBlur={() => {
                      const trimmedTitle = checklistTitle.trim();
                      if (!canManage) {
                        return;
                      }
                      if (!trimmedTitle) {
                        restoreChecklistTitle(index);
                        return;
                      }
                      updateChecklistItem(index, { title: trimmedTitle });
                    }}
                  />
                  <TextField
                    label={t('onboardingDueDate')}
                    type="date"
                    value={item.dueDate ?? ''}
                    disabled={!canManage}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ 'aria-label': `checklist-dueDate-${index}` }}
                    onChange={event =>
                      updateChecklistItem(
                        index,
                        { dueDate: event.target.value || undefined },
                        false
                      )
                    }
                    onBlur={() =>
                      canManage &&
                      updateChecklistItem(index, {
                        dueDate: item.dueDate
                      })
                    }
                  />
                  <Chip
                    label={getOwnerLabel(t, item.ownerType)}
                    size="small"
                    variant="outlined"
                  />
                </InputGroup>
                {canManage && (
                  <IconButton
                    aria-label={`remove-checklist-${index}`}
                    onClick={() =>
                      persistOnboarding({
                        ...draft,
                        checklist: draft.checklist.filter(
                          (_, itemIndex) => itemIndex !== index
                        )
                      })
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </ItemRow>
            );
          })}
          {canManage && (
            <NewItemRow>
              <TextField
                label={t('onboardingChecklistItem')}
                value={newChecklistTitle}
                fullWidth
                inputProps={{ 'aria-label': 'new-checklist-title' }}
                onChange={event => setNewChecklistTitle(event.target.value)}
              />
              <Button
                color="primary"
                startIcon={<AddIcon />}
                disabled={!newChecklistTitle.trim()}
                onClick={() => {
                  const nextOnboarding = {
                    ...draft,
                    checklist: [
                      ...draft.checklist,
                      {
                        title: newChecklistTitle.trim(),
                        done: false,
                        ownerType: newChecklistOwnerType
                      }
                    ]
                  };
                  setNewChecklistTitle('');
                  persistOnboarding(nextOnboarding);
                }}
              >
                {t('onboardingAddChecklistItem')}
              </Button>
            </NewItemRow>
          )}
        </Section>

        <Gap />

        <Section>
          <TitleContainer>
            <Capitalized variant="h5">{t('onboardingDocuments')}</Capitalized>
          </TitleContainer>
          <Line />
          {draft.documents?.length === 0 && (
            <EmptyState variant="body2">
              {t('onboardingDocumentsEmpty')}
            </EmptyState>
          )}
          {(draft.documents ?? []).map((document, index) => {
            const documentTitle = getDocumentTitle(t, document);
            const hasPredefinedDocumentTitle = !!document.documentType;
            const hasDocumentTitleError =
              canManage &&
              !hasPredefinedDocumentTitle &&
              !hasValidTitle(document.title);

            return (
              <ItemRow key={index}>
                <InputGroup>
                  <TextField
                    label={t('onboardingDocument')}
                    value={documentTitle}
                    disabled={!canManage || hasPredefinedDocumentTitle}
                    error={hasDocumentTitleError}
                    helperText={
                      hasDocumentTitleError
                        ? t('onboardingDocumentTitleRequired')
                        : ' '
                    }
                    fullWidth
                    inputProps={{ 'aria-label': `document-title-${index}` }}
                    onChange={event =>
                      updateDocument(
                        index,
                        { title: event.target.value },
                        false
                      )
                    }
                    onBlur={() => {
                      const trimmedTitle = documentTitle.trim();
                      if (!canManage) {
                        return;
                      }
                      if (hasPredefinedDocumentTitle) {
                        return;
                      }
                      if (!trimmedTitle) {
                        restoreDocumentTitle(index);
                        return;
                      }
                      updateDocument(index, {
                        title: trimmedTitle
                      });
                    }}
                  />
                  <TextField
                    select
                    label={t('status')}
                    value={document.status}
                    disabled={!canManage}
                    inputProps={{ 'aria-label': `document-status-${index}` }}
                    onChange={event =>
                      updateDocument(index, {
                        status: event.target.value as OnboardingDocumentStatus
                      })
                    }
                  >
                    {documentStatuses.map(status => (
                      <MenuItem key={status} value={status}>
                        {getDocumentStatusLabel(t, status)}
                      </MenuItem>
                    ))}
                  </TextField>
                </InputGroup>
                {canManage && (
                  <IconButton
                    aria-label={`remove-document-${index}`}
                    onClick={() =>
                      persistOnboarding({
                        ...draft,
                        documents: (draft.documents ?? []).filter(
                          (_, itemIndex) => itemIndex !== index
                        )
                      })
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </ItemRow>
            );
          })}
          {canManage && (
            <NewItemRow>
              <TextField
                label={t('onboardingDocument')}
                value={newDocumentTitle}
                fullWidth
                inputProps={{ 'aria-label': 'new-document-title' }}
                onChange={event => setNewDocumentTitle(event.target.value)}
              />
              <Button
                color="primary"
                startIcon={<AddIcon />}
                disabled={!newDocumentTitle.trim()}
                onClick={() => {
                  const nextOnboarding = {
                    ...draft,
                    documents: [
                      ...(draft.documents ?? []),
                      {
                        title: newDocumentTitle.trim(),
                        status: 'missing' as OnboardingDocumentStatus
                      }
                    ]
                  };
                  setNewDocumentTitle('');
                  persistOnboarding(nextOnboarding);
                }}
              >
                {t('onboardingAddDocument')}
              </Button>
            </NewItemRow>
          )}
        </Section>
      </Container>
      <VerticalContainer />
    </ScrollContainer>
  );
}
